import {
  EllipsisHorizontalIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import type { RunEtaPayload } from "@the-drop/types";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  type ActiveRunFilter,
  type DashboardActiveRun,
  fetchDispensaryActiveRunsApi,
  fetchOrgActiveRunsApi,
  mapApiRunToDashboardRun,
  runMatchesActiveTab,
} from "../lib/dispensary-dashboard-api";
import { useDispensaryRunEta } from "../hooks/useDispensaryRunEta";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";
import { DashboardEmptyState } from "./DashboardEmptyState";
import { RunEtaInline } from "./RunEtaInline";

function avatarUrl(name: string): string {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=F0F0F0&color=333333&size=128`;
}

export function DarkOrdersCard({ headerSearch = "" }: { headerSearch?: string }) {
  const [activeTab, setActiveTab] = useState<ActiveRunFilter>("on_the_way");
  const [search, setSearch] = useState("");
  const { getToken, dispensaryId, orgId, allLocations } = useDispensaryWorkspace();
  const aggregate = allLocations && orgId != null;
  // Live ETA socket is per-store; no live overlay in "All locations" mode.
  const runEtaByRunId = useDispensaryRunEta(aggregate ? null : dispensaryId);

  const { data: activeRunsRaw = [], isLoading } = useQuery({
    queryKey: ["dispensary", "active-runs", aggregate ? `org:${orgId}` : dispensaryId],
    queryFn: () =>
      aggregate
        ? fetchOrgActiveRunsApi(getToken, orgId!)
        : fetchDispensaryActiveRunsApi(getToken, dispensaryId!),
    enabled: Boolean(dispensaryId) || aggregate,
    refetchInterval: 30_000,
  });

  const counts = {
    all: activeRunsRaw.length,
    pending: activeRunsRaw.filter((r) => runMatchesActiveTab(r.status, "pending")).length,
    on_the_way: activeRunsRaw.filter((r) => runMatchesActiveTab(r.status, "on_the_way")).length,
  };

  const activeRuns: DashboardActiveRun[] = activeRunsRaw
    .filter((r) => runMatchesActiveTab(r.status, activeTab))
    .map(mapApiRunToDashboardRun);

  const filtered = useMemo(() => {
    const needle = (headerSearch.trim() || search.trim()).toLowerCase();
    if (!needle) return activeRuns;
    return activeRuns.filter((r) => {
      const hay = [
        r.driver.name,
        r.vehicle.plate,
        r.runId,
        String(r.vehicle.year),
        r.vehicle.make,
        r.vehicle.model,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [activeRuns, headerSearch, search]);

  return (
    <div className="overflow-hidden rounded-2xl border border-[#EBEBEB] shadow-[0_1px_4px_rgba(0,0,0,.06)]">
      <div className="bg-[#111111] px-5 pb-4 pt-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <h3 className="text-base font-bold text-white">Recent Orders</h3>
            <div className="flex flex-wrap items-center gap-1">
              {(
                [
                  { key: "all" as const, label: `All (${counts.all})` },
                  { key: "pending" as const, label: `Pending (${counts.pending})` },
                  { key: "on_the_way" as const, label: `On The Way (${counts.on_the_way})` },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors duration-150 ${
                    activeTab === tab.key
                      ? "bg-white text-[#111111]"
                      : "text-[#888888] hover:text-white"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-white/10"
            aria-label="More options"
          >
            <EllipsisHorizontalIcon className="h-4 w-4 text-[#888888]" />
          </button>
        </div>

        <div className="flex items-center gap-2.5 rounded-xl border border-[#333333] bg-[#1E1E1E] px-4 py-2.5">
          <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0 text-[#666666]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-[#666666]"
          />
        </div>

        <div className="mt-4 grid grid-cols-[1fr_80px_140px_1fr] px-1">
          {["Assigned Transporter", "ETA", "Transporter ETA", "Transporter Status"].map((col) => (
            <span key={col} className="text-2xs font-medium uppercase tracking-wide text-[#666666]">
              {col}
            </span>
          ))}
        </div>
      </div>

      <div className="bg-white">
        {isLoading ? (
          <>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="flex animate-pulse items-center gap-3 border-b border-[#F0F0F0] px-5 py-4"
              >
                <div className="h-9 w-9 flex-shrink-0 rounded-full bg-[#F0F0F0]" />
                <div className="flex-1">
                  <div className="mb-2 h-3 w-32 rounded bg-[#F0F0F0]" />
                  <div className="h-2.5 w-20 rounded bg-[#F0F0F0]" />
                </div>
                <div className="h-3 w-16 rounded bg-[#F0F0F0]" />
              </div>
            ))}
          </>
        ) : filtered.length === 0 ? (
          <DashboardEmptyState
            icon={<TruckIcon className="h-8 w-8 text-[#DDDDDD]" />}
            message={
              activeRuns.length > 0 && (headerSearch.trim() || search.trim())
                ? "No active runs match your search."
                : "No active runs"
            }
          />
        ) : (
          filtered.map((run, i) => (
            <ActiveRunRow
              key={run.runId}
              run={run}
              runEta={runEtaByRunId[run.runId]}
              isLast={i === filtered.length - 1}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ActiveRunRow({
  run,
  runEta,
  isLast,
}: {
  run: DashboardActiveRun;
  runEta: RunEtaPayload | undefined;
  isLast: boolean;
}) {
  const src = run.driver.avatarUrl?.trim() || avatarUrl(run.driver.name);
  return (
    <div
      className={`grid grid-cols-[1fr_140px_140px_1fr] items-center px-5 py-4 transition-colors hover:bg-[#FAFAFA] ${
        !isLast ? "border-b border-[#F0F0F0]" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <img src={src} alt="" className="h-9 w-9 flex-shrink-0 rounded-full object-cover" />
        <div>
          <div className="text-sm font-semibold text-[#111111]">{run.driver.name}</div>
          <div className="mt-0.5 flex items-center gap-1">
            <UserIcon className="h-3 w-3 text-[#AAAAAA]" />
            <span className="text-xs text-[#AAAAAA]">{run.vehicle.plate}</span>
          </div>
        </div>
      </div>
      <RunEtaInline runEta={runEta} fallbackEtaMinutes={run.etaMinutes} />
      <div className="flex items-center gap-2">
        <TruckIcon className="h-5 w-5 text-[#AAAAAA]" />
        <span className="text-sm font-semibold text-[#111111]">
          ${(run.totalLoadValueCents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </span>
      </div>
      <span className="text-sm text-[#555555]">
        {run.vehicle.year} {run.vehicle.make} {run.vehicle.model}
        <br />
        <span className="text-xs text-[#AAAAAA]">#{run.vehicle.plate}</span>
      </span>
    </div>
  );
}
